import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage, useIntl } from 'react-intl';

import { Input, InputWrapper } from '@amzn/stencil-react-components/input';
import { Select } from '@amzn/stencil-react-components/select';
import { FormWrapper } from '@amzn/stencil-react-components/form';
import { Container, Spacer, Col, View } from '@amzn/stencil-react-components/layout';
import { H1 } from '@amzn/stencil-react-components/text';

import { Button } from '@amzn/stencil-react-components/button';
import { permissionLevelOptions } from '../constants/userGroup';
import { registerAccessGroups } from '../actions/userInfoActions';
import { registerTeamResponseSelector, userDetailsSelector } from '../selectors/selectors';
import { ResponseStatus } from '../enums/responseStatus';
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner';

/**
 * Component that will display the onboarding configuration for users
 * @returns
 */
const OnboardGroup = () => {
  const permissionLevelOption = permissionLevelOptions;
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const userInfo = useSelector(userDetailsSelector);
  const { status, registered } = useSelector(registerTeamResponseSelector);

  const [accessGroupName, setAccessGroupName] = useState();
  const [groupId, setGroupId] = useState();
  const [permissionLevel, setPermissionLevel] = useState();
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [saveStatus, setSaveStatus] = useState();

  const onSubmitHandler = async () => {
    const payload = {
      accessGroupName,
      groupId,
      permissionLevel,
    };
    await dispatch(registerAccessGroups(payload, userInfo.idToken));
    setIsFormSubmitted(true);
  };

  useEffect(
    () => {
      if (registered) {
        setSaveStatus(ResponseStatus.SUCCESS);
      } else {
        setSaveStatus(ResponseStatus.ERROR);
      }
    },
    [status, registered],
  );

  useEffect(
    () => {
      setIsFormSubmitted(false);
    },
    [accessGroupName, groupId, permissionLevel],
  );

  const isFormValid = isEmpty(accessGroupName) || isEmpty(groupId) || isEmpty(permissionLevel);

  return (
    <Container width="98%">
      <View
        alignItems="center"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gridGap={20}
        width="70%"
      >
        <Col gridGap="S500" width="100%">
          <H1>
            <FormattedMessage id="app.onboardComponent.heading" defaultMessage="Onboard team" />
          </H1>
          <FormWrapper alignItems="" display="flex" flex={1} justifyContent="center">
            <InputWrapper
              labelText={formatMessage({ id: 'app.onboardGroupName.accessGroupNameLabel' })}
              id="input-id-5"
            >
              {inputProps => (
                <Input
                  data-testid="team-name-input"
                  onChange={e => setAccessGroupName(e.target.value)}
                  {...inputProps}
                />
              )}
            </InputWrapper>

            <InputWrapper
              labelText={formatMessage({ id: 'app.onboardGroupName.groupId' })}
              id="input-id-5"
            >
              {inputProps => (
                <Input
                  data-testid="group-id-input"
                  onChange={e => setGroupId(e.target.value)}
                  {...inputProps}
                />
              )}
            </InputWrapper>

            <InputWrapper
              id="some-input-id"
              labelText={formatMessage({ id: 'app.onboardGroupName.permissionLevel' })}
            >
              {inputProps => (
                <Select
                  onChange={e => setPermissionLevel(e)}
                  options={permissionLevelOption}
                  placeholder={formatMessage({ id: 'app.onboardGroupName.choosePermissionLevel' })}
                  data-testid="permission-dropdown"
                  {...inputProps}
                />
              )}
            </InputWrapper>
          </FormWrapper>
        </Col>
        <Spacer height={20} />
        <Col display="flex" flexDirection="row" gridGap={20}>
          <Button
            disabled={isFormValid || isFormSubmitted}
            data-testid="team-submit-btn"
            primary
            onClick={onSubmitHandler}
          >
            <FormattedMessage id="app.onboardGroupComponent.submitBtn" defaultMessage="Submit" />
          </Button>
        </Col>
        <Spacer height={20} />
        {isFormSubmitted && (
          <>
            {saveStatus === ResponseStatus.SUCCESS && (
              <MessageBanner data-testid="message-banner" type={MessageBannerType.Error}>
                <FormattedMessage id="app.onboardGroupName.error" />
              </MessageBanner>
            )}
            {saveStatus === ResponseStatus.ERROR && (
              <MessageBanner data-testid="message-banner" type={MessageBannerType.Success}>
                <FormattedMessage id="app.onboardGroupName.success" />
              </MessageBanner>
            )}
            <Spacer height={20} />
          </>
        )}
      </View>
    </Container>
  );
};

export default OnboardGroup;
